import { IGuide, viewGuide } from 'store/siteGuide/SiteGuideSlice';
import { ChangeEvent, Fragment, useEffect, useState } from 'react';
import { connect, MapDispatchToProps } from 'react-redux';
import { addWatched } from 'store/siteGuide/SiteGuideSlice';
import messages from './messages';
import { appDispatch } from 'store/store';
import i18n, { languageDirection } from '../../i18n';
import LocalIcon from '../../ui/LocalIcon';
import GuideModal from '../GuideModal/GuideModal';

interface IGuideFloatButton {
  allGuides: Array<IGuide>;
  relatedGuides: Array<IGuide>;
  activeGuide?: string;
}

interface IDispatchToProps {
  addWatchedGuide: (guideKey: string) => void;
  viewGuide: (id: string | undefined) => void;
}

const FloatBox = (props: IGuideFloatButton) => {
  const { allGuides, relatedGuides, activeGuide } = props;
  const [activeTab, setActiveTab] = useState<'all' | 'related'>('all');
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    if (relatedGuides.length > 0) setActiveTab('related');
  }, [relatedGuides]);
  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    setSearchValue(e.target.value);
    if (e.target.value) {
      setActiveTab('all');
    }
  }

  return (
    <div
      className="float-box"
      style={{
        left: languageDirection() === 'rtl' ? '4rem' : 'initial',
        right: languageDirection() === 'rtl' ? 'initial' : '4rem',
      }}
    >
      <div className="search-box">
        <span className="search-box__title">
          {i18n.t('guides', { ns: 'general' }) as string}:
        </span>
        <input
          autoComplete="off"
          className="search-box__input"
          name="search"
          placeholder={i18n.t('searchHere', { ns: 'general' }) as string}
          onChange={onInputChange}
        />
      </div>
      <div className="tab-box">
        <span
          className={`tab-box__button ${activeTab === 'all' ? '--active' : ''}`.trim()}
          onClick={() => {
            setActiveTab('all');
          }}
        >
          {i18n.t('all', { ns: 'general' }) as string}
        </span>
        <span
          className={`tab-box__button ${activeTab === 'related' ? '--active' : ''}`.trim()}
          onClick={() => {
            setActiveTab('related');
          }}
        >
          {messages.relatedTabButton(relatedGuides.length)}
        </span>
      </div>
      <GuideCardsWrapper
        guides={activeTab === 'all' ? allGuides : relatedGuides}
        isAll={activeTab === 'all'}
        searchKey={searchValue}
        activeGuide={activeGuide}
      />
    </div>
  );
};

export default FloatBox;

const GuideCardsWrapper = (props: {
  guides: Array<IGuide>;
  isAll: boolean;
  searchKey: string;
  activeGuide?: string;
}) => {
  const { guides, isAll, searchKey, activeGuide } = props;
  if (isAll) {
    const groupedGuides = {};
    guides.forEach((guide) => {
      guide.group.forEach((group) => {
        if (!groupedGuides[group]) {
          groupedGuides[group] = [];
        }

        const searchKeysArray = searchKey.split(' ');
        const isSearchResult = searchKeysArray.every((key) => {
          return guide.title.toLowerCase().includes(key.toLowerCase());
        });

        if (isSearchResult) {
          let highlightedTitle = guide.title;
          searchKeysArray.forEach((key) => {
            if (key) {
              highlightedTitle = highlightedTitle.replaceAll(
                key,
                `<strong class="font-black">${key}</strong>`
              );
            }
          });
          groupedGuides[group].push({
            ...guide,
            title: highlightedTitle,
            originalTitle: guide.title,
          });
        }
      });
    });

    return (
      <div className="guide-cards">
        <>
          <p className="guide-card__group-title flex">
            <a
              className={'guide-card --not-watched'}
              style={{
                display: 'felx',
                alignItems: 'center',
                justifyContent: 'start',
                width: '100%',
                background:
                  'linear-gradient(\
                    96deg,\
                rgb(66, 97, 253) 0%,\
                rgb(244, 75, 109) 100%\
                )',
              }}
              href="https://app.rastech.ir/help#chat"
              target={'_blank'}
              rel="noreferrer"
            >
              <LocalIcon iconName={'ai-bot.svg'} type={'sm'} />
              <span className="guide-card__title" style={{ flex: '1' }}>
                {i18n.t('AskChatbot', { ns: 'general' }) as string}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M4.00977 11.98H18.9998"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.013 5.98804L4.00195 12L10.013 18.012"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </p>
        </>
        {Object.keys(groupedGuides).map((key: string, idx: number) => {
          return groupedGuides[key].length > 0 ? (
            <Fragment key={`wrapper-${idx}`}>
              <p className="guide-card__group-title">{key}</p>
              {groupedGuides[key].map(
                (guide: IGuide & { originalTitle: string }, idx: number) => {
                  return (
                    <GuideCardWithConnect
                      guide={guide}
                      key={`guide-${idx}`}
                      isOpen={activeGuide === guide.key}
                    />
                  );
                }
              )}
            </Fragment>
          ) : null;
        })}
      </div>
    );
  } else {
    return (
      <div className="guide-cards">
        {guides.map((item, idx: number) => (
          <GuideCardWithConnect
            guide={{ ...item, originalTitle: item.title }}
            key={`card-${idx}`}
            isOpen={activeGuide === item.key}
          />
        ))}
      </div>
    );
  }
};

const GuideCard = (
  props: {
    guide: IGuide & { originalTitle: string };
    isOpen: boolean;
  } & IDispatchToProps
) => {
  const { guide, addWatchedGuide, isOpen, viewGuide } = props;
  const [isModalActive, setIsModalActive] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsModalActive(true);
    }
    setTimeout(() => {
      viewGuide(undefined);
    }, 2000);
  }, [isOpen, viewGuide]);

  const handleClose = async () => {
    setIsModalActive(false);
    await addWatchedGuide(guide.key);
  };

  return (
    <Fragment>
      <GuideModal
        guideLink={guide.link}
        guideTitle={guide.originalTitle}
        isOpen={isModalActive}
        onClose={handleClose}
      />
      <div
        className={`guide-card ${guide.isWatched ? '' : '--not-watched'}`.trim()}
        onClick={() => setIsModalActive(true)}
      >
        <span
          className="guide-card__title"
          dangerouslySetInnerHTML={{ __html: guide.title }}
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M4.00977 11.98H18.9998"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.013 5.98804L4.00195 12L10.013 18.012"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </Fragment>
  );
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  { guide: IGuide & { originalTitle: string } }
> = () => ({
  addWatchedGuide: (guideKey: string) => {
    appDispatch(addWatched(guideKey));
  },
  viewGuide: (guideKey: string | undefined) => {
    appDispatch(viewGuide(guideKey));
  },
});

const GuideCardWithConnect = connect(null, mapDispatchToProps)(GuideCard);
